import {
  type QueryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiError } from '../../../../utils/api/ApiError/ApiError';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import { productShape } from '../../../../utils/api/types/access-api-types';

export const classSubscriptionsShape = z.array(
  z.object({
    enabled: z.boolean().optional(),
    entitlementId: z.string().optional(),
    subscriptionId: z.string(),
    orderId: z.string().optional(),
    endDate: z.string().datetime(),
    entitlementType: z.string().optional(),
    ownerUserId: z.string().optional(),
    ownerType: z.string().optional(),
    product: productShape,
  })
);

export type ClassSubscription = z.infer<typeof classSubscriptionsShape>[0];

export interface GetClassSubscriptionsParams {
  orgId: string | undefined;
  classId: string | undefined;
  enabled?: boolean;
}

export async function getSubscriptionsByClassIdApi({
  orgId,
  classId,
  enabled,
}: GetClassSubscriptionsParams): Promise<ClassSubscription[]> {
  const classSubscriptions = await fetcher(
    `/composite/subscriptions/orgs/${orgId}/class/${classId}`,
    {
      service: '/fulfillment',
      version: '/v2',
      queryParameters:
        enabled !== undefined ? { enabled: enabled.toString() } : undefined,
    }
  );

  return classSubscriptionsShape.parse(classSubscriptions);
}

export const getSubscriptionsByClassIdApiQueryKey = (
  args?: GetClassSubscriptionsParams
) => {
  const { orgId, classId, enabled } = args ?? {};
  return ['/v2', '/composite/subscriptions', orgId, classId, enabled].filter(
    Boolean
  );
};

export function useGetSubscriptionsByClassIdApi(
  params: GetClassSubscriptionsParams,
  customQueryOptions?: QueryOptions<ClassSubscription[], ApiError>
): UseQueryResult<ClassSubscription[], ApiError> {
  return useQuery({
    queryKey: getSubscriptionsByClassIdApiQueryKey(params),
    queryFn: () => getSubscriptionsByClassIdApi(params),
    enabled: !!params.orgId && !!params.classId,
    ...customQueryOptions,
  });
}
